import type { BaseUser } from "@models";
import { Box, Button, Paper, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { UserPickerOption } from "src/components/UserPicker";

interface CommentInputProps {
  onSubmit: (content: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
  orgUsers?: BaseUser[];
  initialValue?: string;
  showActionsOnFocusOnly?: boolean;
}

type MentionReplacement = {
  displayText: string;
  email: string;
};

export const CommentInput = ({
  onSubmit,
  onCancel,
  placeholder = "Add a comment... Use @ to mention someone",
  submitLabel = "Comment",
  autoFocus = false,
  orgUsers = [],
  initialValue = "",
  showActionsOnFocusOnly = false,
}: CommentInputProps) => {
  const [content, setContent] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionReplacements, setMentionReplacements] = useState<
    MentionReplacement[]
  >([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  const mentionItemsRef = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
      // Move cursor to end of text
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [autoFocus]);

  // Close mention dropdown when clicking outside of it and the input
  useEffect(() => {
    if (!showMentions) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        setShowMentions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMentions]);

  // If choosing a mention with arrow keys, keep the selected mention in view
  useEffect(() => {
    if (showMentions && mentionItemsRef.current[selectedMentionIndex]) {
      mentionItemsRef.current[selectedMentionIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [selectedMentionIndex, showMentions]);

  const filteredUsers = orgUsers.filter(
    (user) =>
      user.firstName?.toLowerCase().startsWith(mentionFilter) ||
      user.lastName?.toLowerCase().startsWith(mentionFilter) ||
      user.email.toLowerCase().startsWith(mentionFilter)
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setContent(value);
    setMentionReplacements((previous) =>
      previous.filter(({ displayText }) => value.includes(displayText))
    );

    // Use selectionStart when available and positive, otherwise search full value.
    // In controlled MUI textareas, selectionStart on the event target can be
    // unreliable (reset to 0 during React reconciliation), so we always fall
    // back to searching the entire value for the active mention fragment.
    const rawCursor = e.target.selectionStart;
    const searchText =
      rawCursor != null && rawCursor > 0
        ? value.substring(0, rawCursor)
        : value;

    // Find the last @mention fragment at the end of the search text.
    // The $ anchor ensures we match only the fragment the user is actively typing.
    const mentionMatch = searchText.match(/@([\w.+@-]*)$/);

    if (
      mentionMatch &&
      mentionMatch.index != null &&
      mentionMatch[1].length < 50
    ) {
      setShowMentions(true);
      setMentionFilter(mentionMatch[1].toLowerCase());
      setMentionCursorPosition(mentionMatch.index);
      setSelectedMentionIndex(0);
    } else {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (user: BaseUser) => {
    const beforeMention = content.substring(0, mentionCursorPosition);
    const afterMention = content.substring(
      mentionCursorPosition + mentionFilter.length + 1
    );
    const displayName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const displayText = `@${displayName || user.email}`;
    const mentionText = `${displayText} `;

    setContent(beforeMention + mentionText + afterMention);
    setMentionReplacements((previous) => [
      ...previous.filter((mention) => mention.displayText !== displayText),
      { displayText, email: user.email },
    ]);
    setShowMentions(false);

    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSubmit = () => {
    const trimmedContent = content.trim();
    // Don't submit if empty or unchanged from initial value
    if (trimmedContent && trimmedContent !== initialValue.trim()) {
      const submittedContent = mentionReplacements
        .slice()
        .sort((a, b) => b.displayText.length - a.displayText.length)
        .reduce(
          (value, { displayText, email }) =>
            value.split(displayText).join(`@${email}`),
          trimmedContent
        );
      onSubmit(submittedContent);
      setContent("");
      setMentionReplacements([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions && filteredUsers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIndex((prev) =>
          Math.min(prev + 1, filteredUsers.length - 1)
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIndex((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const user = filteredUsers[selectedMentionIndex];
        if (user) {
          handleMentionSelect(user);
        }
        return;
      }
    }

    // Submit only on Cmd/Ctrl + Enter. Plain Enter inserts a newline.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
      return;
    }

    if (e.key === "Escape") {
      if (showMentions) {
        // Close the mention menu; MUI Menu's onClose handles click-outside,
        // but Escape must be handled here because focus stays on the TextField.
        setShowMentions(false);
      } else {
        onCancel?.();
      }
    }
  };

  const showActions =
    !showActionsOnFocusOnly || isFocused || content.trim() !== "";

  const handleCancel = () => {
    if (showActionsOnFocusOnly) {
      setContent("");
      setMentionReplacements([]);
      inputRef.current?.blur();
    }
    onCancel?.();
  };

  return (
    <Box className="relative">
      <Box sx={{ position: "relative" }}>
        <TextField
          inputRef={inputRef}
          value={content}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          multiline
          minRows={1}
          maxRows={6}
          sx={{
            "& .MuiInputBase-root": {
              padding: 0,
            },
            "& textarea:not([aria-hidden])": {
              overflowY: "auto !important",
              padding: "8px 12px",
            },
          }}
          fullWidth
          size="small"
          hiddenLabel
        />

        {/* Mention suggestions dropdown */}
        {showMentions && filteredUsers.length > 0 && (
          <Paper
            ref={mentionDropdownRef}
            elevation={4}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1500,
              mt: 0.5,
              maxHeight: 300,
              minWidth: 200,
              overflowY: "auto",
              boxShadow: 3,
            }}
          >
            {filteredUsers.map((user, index) => (
              <UserPickerOption
                key={user.id}
                user={user}
                highlighted={index === selectedMentionIndex}
                itemRef={(el) => {
                  mentionItemsRef.current[index] = el;
                }}
                onMouseEnter={() => setSelectedMentionIndex(index)}
                onClick={(_, selectedUser) => handleMentionSelect(selectedUser)}
              />
            ))}
          </Paper>
        )}
      </Box>

      {showActions && (
        <Box className="mt-2 flex items-center justify-end w-full">
          {onCancel && (
            <Button
              size="small"
              onClick={handleCancel}
              variant="text"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={!content.trim() || content.trim() === initialValue.trim()}
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            {submitLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
};
